import type {
  SpotifyCanvasFileRefusal,
  SpotifyCanvasSongResponse,
  SpotifyCanvasUpload,
} from 'fym-shared/src/music24/spotifyCanvas';

/** The chips after the name (design spotify-canvas a2). */
export type CanvasChip =
  | 'onSpotify'
  | 'making'
  | 'uploading'
  | 'waits'
  | 'failed'
  | 'signedOut'
  | 'inReview'
  | 'hasCanvas'
  | 'auto';

/** What a button or a link of the row does (design spotify-canvas a2, a3). */
export type CanvasAction =
  | 'connect'
  | 'reconnect'
  | 'makeCanvas'
  | 'makeNewCanvas'
  | 'upload'
  | 'uploadIt'
  | 'retry'
  | 'replace'
  | 'open';

/** `primary`: btn-primary. `button`: the plain button. `link`: a text link. */
export type CanvasActionLook = 'primary' | 'button' | 'link';

export interface CanvasRowAction {
  action: CanvasAction;
  look: CanvasActionLook;
}

/** The second line of the row. The ids are the keys of `spotifyCanvasMessages`. */
export type CanvasSentenceId =
  | 'notConnected'
  | 'reconnect'
  | 'noCanvas'
  | 'rendering'
  | 'noTrack'
  | 'notLiveAuto'
  | 'notLive'
  | 'ready'
  | 'uploading'
  | 'inReview'
  | 'onSpotify'
  | 'onSpotifyAuto'
  | 'onSpotifyVersion'
  | 'failReconnect'
  | 'failNoPermission'
  | 'failNotLive'
  | 'failFile'
  | 'failRefused'
  | 'failRejected'
  | 'failFileGone'
  | 'failError'
  | 'existingCanvas';

export interface CanvasSentence {
  id: CanvasSentenceId;
  /** ISO time of the "Sent on" sentences. */
  date?: string;
  /** The version on Spotify (`onSpotifyVersion`). */
  revision?: number;
  /** The Spotify text (`failRefused`), or null. */
  detail?: string | null;
  /** The broken file rule (`failFile`), or null when Spotify did not say. */
  rule?: SpotifyCanvasFileRefusal | null;
}

/** The tile: the loop, a dashed box (no loop), or the loop dimmed (it renders). */
export type CanvasThumb = 'loop' | 'empty' | 'dim';

export interface SpotifyCanvasRowView {
  thumb: CanvasThumb;
  /** The small Spotify dot on the tile: the loop is on Spotify. */
  dot: boolean;
  chips: CanvasChip[];
  sentence: CanvasSentence;
  /** The sentence is in the neg colour (failed, reconnect). */
  isBad: boolean;
  /** The line "A newer Canvas is ready." */
  newer: boolean;
  /** The Watermark line of the Free plan. */
  watermark: boolean;
  /** The bar with no percent (rendering, uploading). */
  progress: boolean;
  actions: CanvasRowAction[];
  /**
   * An action of the artist waits: the "Spotify extras" box opens by itself (design spotify-canvas
   * d3 fold rule: notConnected, reconnect, noCanvas, noTrack, ready, a newer Canvas, failed,
   * existingCanvas). It folds for rendering, uploading, inReview, onSpotify and notLive.
   */
  waits: boolean;
}

const RULES: readonly SpotifyCanvasFileRefusal[] = ['type', 'size', 'tooShort', 'tooLong', 'aspect', 'tooSmall'];

/** The file rule of a `failDetail`, or null for a text that is no rule. */
export function canvasFileRule(detail: string | null | undefined): SpotifyCanvasFileRefusal | null {
  return RULES.find((rule) => rule === detail) ?? null;
}

function sentAt(upload: SpotifyCanvasUpload): string {
  return upload.publishedAt ?? upload.updatedAt ?? upload.createdAt;
}

const primary = (action: CanvasAction): CanvasRowAction => ({ action, look: 'primary' });
const button = (action: CanvasAction): CanvasRowAction => ({ action, look: 'button' });
const OPEN: CanvasRowAction = { action: 'open', look: 'link' };

type Draft = Omit<SpotifyCanvasRowView, 'waits'>;

function plain(thumb: CanvasThumb, sentence: CanvasSentence, extra: Partial<Draft> = {}): Draft {
  return {
    thumb,
    dot: false,
    chips: [],
    sentence,
    isBad: false,
    newer: false,
    watermark: false,
    progress: false,
    actions: [],
    ...extra,
  };
}

/** The "On Spotify" look: onSpotify, and a newer Canvas (design spotify-canvas a2, a4). */
function liveView(data: SpotifyCanvasSongResponse, upload: SpotifyCanvasUpload, newer: boolean): Draft {
  const open = data.spotifyTrackId === null ? [] : [OPEN];
  const chips: CanvasChip[] = upload.auto ? ['onSpotify', 'auto'] : ['onSpotify'];
  if (newer) {
    return plain(
      'loop',
      { id: 'onSpotifyVersion', revision: upload.revision, date: sentAt(upload) },
      { dot: true, chips, newer: true, actions: [primary('uploadIt'), ...open] }
    );
  }
  return plain(
    'loop',
    { id: upload.auto ? 'onSpotifyAuto' : 'onSpotify', date: sentAt(upload) },
    { dot: true, chips, actions: open }
  );
}

/** A failed record: one sentence and one action for each fail reason (design spotify-canvas a3). */
function failedView(data: SpotifyCanvasSongResponse, upload: SpotifyCanvasUpload | null, thumb: CanvasThumb): Draft {
  const failed = (sentence: CanvasSentence, action: CanvasRowAction, extra: Partial<Draft> = {}): Draft =>
    plain(thumb, sentence, { chips: ['failed'], isBad: true, actions: [action], ...extra });
  switch (upload?.failReason ?? 'error') {
    case 'existingCanvas':
      // Not an error: the amber chip and a plain sentence. "Replace it" is a click upload.
      return plain(thumb, { id: 'existingCanvas' }, { chips: ['hasCanvas'], actions: [button('replace')] });
    case 'reconnect': {
      // After the new sign-in the row offers Try again (design a3 note).
      const usable = data.connection.connected && !data.connection.needsReconnect;
      return failed({ id: 'failReconnect' }, primary(usable ? 'retry' : 'reconnect'));
    }
    case 'noPermission':
      return failed({ id: 'failNoPermission' }, button('retry'));
    case 'notLive':
      return failed({ id: 'failNotLive' }, button('retry'));
    case 'file':
      return failed({ id: 'failFile', rule: canvasFileRule(upload?.failDetail) }, button('makeNewCanvas'));
    case 'refused':
      return failed({ id: 'failRefused', detail: upload?.failDetail ?? null }, button('retry'));
    case 'rejected':
      return failed({ id: 'failRejected' }, button('makeNewCanvas'));
    case 'fileGone':
      return failed({ id: 'failFileGone' }, button('makeCanvas'), { thumb: 'empty' });
    case 'error':
      return failed({ id: 'failError' }, primary('retry'));
  }
}

function draftOf(data: SpotifyCanvasSongResponse): Draft {
  const { upload } = data;
  const thumb: CanvasThumb = data.canvas === null ? 'empty' : data.canvas.ready ? 'loop' : 'dim';
  switch (data.state) {
    case 'notConnected':
      return plain(thumb, { id: 'notConnected' }, { actions: [button('connect')] });
    case 'reconnect':
      return plain(thumb, { id: 'reconnect' }, { chips: ['signedOut'], isBad: true, actions: [primary('reconnect')] });
    case 'noCanvas':
      return plain('empty', { id: 'noCanvas' }, { actions: [button('makeCanvas')] });
    case 'rendering':
      return plain('dim', { id: 'rendering' }, { chips: ['making'], progress: true });
    case 'noTrack':
      // The Spotify row above has Paste link and Find it (design a2 note).
      return plain(thumb, { id: 'noTrack' });
    case 'notLive':
      return data.settings.autoUpload
        ? plain(thumb, { id: 'notLiveAuto' }, { chips: ['waits', 'auto'] })
        : plain(thumb, { id: 'notLive' });
    case 'ready':
      // newerCanvas: the API answers `ready` with an upload that is `published` (design a2 note).
      if (data.newerCanvas && upload !== null && upload.status === 'published') {
        return liveView(data, upload, true);
      }
      return plain(
        thumb,
        { id: 'ready' },
        { watermark: data.canvas?.watermark === true, actions: [primary('upload')] }
      );
    case 'uploading':
      return plain(thumb, { id: 'uploading' }, { chips: ['uploading'], progress: true });
    case 'inReview':
      return plain(thumb, { id: 'inReview' }, { chips: ['inReview'] });
    case 'onSpotify':
      return upload === null ? plain(thumb, { id: 'uploading' }) : liveView(data, upload, false);
    case 'failed':
      return failedView(data, upload, thumb);
  }
}

/** The states in which nothing waits for the artist: the extras box folds (design d3). */
const QUIET_STATES: ReadonlySet<SpotifyCanvasSongResponse['state']> = new Set([
  'rendering',
  'uploading',
  'inReview',
  'notLive',
  'onSpotify',
]);

/** True when the Canvas line waits for an action of the artist (design spotify-canvas d3). */
export function canvasWaitsForArtist(data: SpotifyCanvasSongResponse): boolean {
  return !QUIET_STATES.has(data.state);
}

/**
 * What the Canvas line of "Spotify extras" shows for one answer of `GET .../spotify-canvas` (design
 * spotify-canvas a2, a3, d3). Pure. The demo artist (`readOnly`) sees the line with no button; its
 * auto upload switch is dimmed.
 */
export function spotifyCanvasRowView(data: SpotifyCanvasSongResponse, readOnly: boolean): SpotifyCanvasRowView {
  const draft = draftOf(data);
  const waits = canvasWaitsForArtist(data);
  if (readOnly) {
    return { ...draft, actions: [], waits };
  }
  return { ...draft, waits };
}

/** The states that wait for Spotify or for the render: the row polls while the socket is down. */
export function canvasRowWaits(data: SpotifyCanvasSongResponse | undefined): boolean {
  return data !== undefined && (data.state === 'rendering' || data.state === 'uploading' || data.state === 'inReview');
}

/** `https://open.spotify.com/track/<id>` (design spotify-canvas a2 "Open on Spotify"). */
export function spotifyTrackUrl(spotifyTrackId: string): string {
  return `https://open.spotify.com/track/${encodeURIComponent(spotifyTrackId)}`;
}
